/**
 * A message box with its tools inside the frame, the way chat composers are built: the text,
 * then one slim row with the attach button and file chips on the left and Send on the right.
 * Files dropped anywhere on it attach (AttachmentInput variant="button" finds the box through
 * data-drop-scope). Ctrl or Cmd Enter sends.
 */
import { useId } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface ComposerProps extends Omit<ComponentProps<'textarea'>, 'onSubmit' | 'children'> {
  /** Called on Ctrl or Cmd Enter. */
  onSubmit?: () => void;
  /** Left of the footer row: the attach button and file chips. */
  tools?: ReactNode;
  /** Right of the footer row: the send button. */
  send: ReactNode;
  /** One short dim line under the box: a result, or why sending is blocked. */
  note?: ReactNode;
  /** Shown beside Send on wide screens, such as the shortcut. */
  hint?: ReactNode;
  className?: string;
}

export function Composer({ onSubmit, tools, send, note, hint, className, rows = 2, id: idProp, onKeyDown, ...rest }: ComposerProps) {
  const auto = useId();
  const id = idProp ?? auto;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div data-drop-scope="" className="control-shell flex flex-col">
        <textarea
          id={id}
          rows={rows}
          aria-describedby={note ? `${id}-note` : undefined}
          className="field-sizing-content max-h-60 min-h-12 w-full resize-none bg-transparent px-3 pt-2 text-body text-fg outline-none placeholder:text-fg-subtle disabled:opacity-50 pointer-coarse:text-(length:--touch-input-font)"
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (!e.defaultPrevented && e.key === 'Enter' && (e.ctrlKey || e.metaKey) && onSubmit) {
              e.preventDefault();
              onSubmit();
            }
          }}
          {...rest}
        />
        <div className="flex min-w-0 items-center gap-2 px-1.5 pb-1.5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">{tools}</div>
          {hint && <span className="hidden shrink-0 text-caption text-fg-subtle sm:inline">{hint}</span>}
          {send}
        </div>
      </div>
      {note && (
        <p id={`${id}-note`} className="text-caption text-fg-subtle">
          {note}
        </p>
      )}
    </div>
  );
}
